import { AnimatePresence, motion } from 'motion/react';
import { Plus, Trash2, Repeat } from 'lucide-react';
import { Subscription } from '../lib/types';
import { cn, formatCurrency } from '../lib/utils';

interface SubscriptionsTabProps {
  subscriptions: Subscription[];
  localCurrency: string;
  onAddSubscription: () => void;
  onDeleteSubscription: (id: string) => void;
  isPrivacyMode: boolean;
}

export function SubscriptionsTab({ subscriptions, localCurrency, onAddSubscription, onDeleteSubscription, isPrivacyMode }: SubscriptionsTabProps) {
  const totalMonthly = subscriptions.reduce((sum, sub) => sum + sub.amount, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between px-1">
        <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">Recurring Payments</h2>
        <button onClick={onAddSubscription} className="flex items-center gap-2 px-4 py-2 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-xl text-xs font-bold uppercase tracking-wider shadow-sm hover:opacity-90 transition-opacity">
          <Plus className="w-4 h-4" />
          Add New
        </button>
      </div>

      <div className="bg-zinc-900 dark:bg-zinc-800 text-white p-8 rounded-[32px] shadow-2xl shadow-zinc-200 dark:shadow-zinc-900/50 transition-colors">
        <div className="text-center space-y-2">
          <p className="text-zinc-400 text-[10px] font-bold uppercase tracking-widest">Monthly Commitment</p>
          <h2 className={cn("text-4xl font-bold tracking-tight", isPrivacyMode && "blur-md select-none")}>
            {formatCurrency(totalMonthly, localCurrency)}
          </h2>
          <p className={cn("text-sm text-zinc-400", isPrivacyMode && "blur-sm select-none")}>across {subscriptions.length} subscriptions</p>
        </div>
      </div>

      <div className="space-y-3">
        {subscriptions.length === 0 ? (
          <div className="text-center py-12 text-zinc-400 text-sm">
            <Repeat className="w-12 h-12 mx-auto mb-3 opacity-20" />
            <p>No recurring payments found.</p>
          </div>
        ) : (
          <AnimatePresence mode="popLayout">
            {subscriptions.map(sub => (
              <motion.div
                layout
                key={sub.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white dark:bg-zinc-900 p-5 rounded-3xl border border-zinc-100 dark:border-zinc-800 shadow-sm flex items-center justify-between group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-2xl bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                    <Repeat className="w-5 h-5" />
                  </div>
                  <div>
                    <h4 className="font-bold text-zinc-900 dark:text-zinc-100">{sub.note}</h4>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400 font-medium">{sub.category}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <p className={cn("font-bold text-zinc-900 dark:text-zinc-100", isPrivacyMode && "blur-sm select-none")}>
                    {formatCurrency(sub.amount, localCurrency)}
                    <span className="text-[10px] text-zinc-400 font-normal ml-1">/mo</span>
                  </p>
                  <button 
                    onClick={() => onDeleteSubscription(sub.id)}
                    className="p-2 text-zinc-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-xl transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
}